import { Switch } from "@mui/material";
import { Pencil } from "lucide-react";
import * as React from "react";

import { useNavigation } from "@/providers";
import { AlertDialog } from "@/shared/components";
import {
  FormCount,
  FormDivider,
  FormField,
  FormLinkButton,
  FormRow,
} from "@/shared/components/FormSection";
import { AgentLink } from "@/shared/components/links/AgentLink";
import { SlackChannelLink } from "@/shared/components/links/SlackChannelLink";
import {
  browserTimeZone,
  entityLabel as labelOfEntity,
  entityOfSubscription,
  eventLabel,
  isSchedule,
  OncePer,
  oncePerChoices,
  readableCondition,
  readableCron,
  readableSchedule,
  runsOnPhrase,
  scheduleFromUtcCron,
  sourceEventTypes,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type PlatformEventType,
  type TriggerSource,
  type TriggerTargetSpec,
  type TriggerV2Record,
} from "@/shared/domain/triggers";
import {
  useDeleteTriggerV2,
  useSetTriggerV2Enabled,
} from "@/shared/domain/triggers/hooks";
import { FormSection } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import { isEditableInForm } from "../TriggerForm/formScope";

import { SampleEventDialog } from "./SampleEventDialog";
import { countClauses } from "./triggerConditionFilters";
import { TriggerFacts } from "./TriggerFacts";
import styles from "./TriggerOverview.module.css";
import { TriggerSentence } from "./TriggerSentence";

/**
 * The Details tab: the trigger in the same sections the create form
 * uses, read rather than edited, so a reader who has just built one finds
 * the same shape here. The sentence leads as the summary. Edit opens the form
 * on its own route; enable and delete sit in a row of their own because they
 * are lifecycle, not properties of the trigger.
 */
export function TriggerOverview({ record }: { record: TriggerV2Record }) {
  const { goto } = useNavigation();

  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [sampleOpen, setSampleOpen] = React.useState(false);

  const setEnabled = useSetTriggerV2Enabled();
  const deleteTrigger = useDeleteTriggerV2();

  const schedule = isSchedule(record.fires_on) ? record.fires_on : null;
  const subscription = isSchedule(record.fires_on) ? null : record.fires_on;
  const clauseCount = countClauses(record.condition ?? null);
  const editable = isEditableInForm(record);

  return (
    <div className={styles.page}>
      {errorText !== null && <div className={styles.error}>{errorText}</div>}

      <div className={styles.actions}>
        <label className={styles.enable}>
          <Switch
            size="small"
            checked={record.enabled}
            disabled={setEnabled.isPending}
            onChange={(_event, checked) => {
              setErrorText(null);
              setEnabled.mutate(
                { name: record.name, enabled: checked },
                { onError: (error) => setErrorText(error.message) },
              );
            }}
            inputProps={{ "aria-label": "Enabled" }}
            data-cy="trigger-enabled-switch"
          />
          <span className={styles.enableText}>
            <span className={styles.enableLabel}>
              {record.enabled ? "Enabled" : "Disabled"}
            </span>
            <span className={styles.enableHint}>
              {record.enabled
                ? "Fires when its platform events match."
                : "Nothing fires until it is enabled again."}
            </span>
          </span>
        </label>
        <div className={styles.spacer} />
        {!editable && (
          <p className={styles.editNote} id="edit-trigger-note">
            This trigger watches something the form cannot show yet, so it is
            edited through the SDK.
          </p>
        )}
        <RobotoButton
          variant="secondary"
          icon={<Pencil aria-hidden="true" />}
          disabled={!editable}
          aria-describedby={editable ? undefined : "edit-trigger-note"}
          onClick={() => goto.editTrigger({ triggerId: record.name })}
          id="edit-trigger-button"
          data-testid="edit-trigger-button"
        >
          Edit
        </RobotoButton>
        <RobotoButton
          variant="secondary"
          className={styles.danger}
          onClick={() => setDeleteOpen(true)}
          id="delete-trigger-button"
        >
          Delete
        </RobotoButton>
      </div>

      <FormSection title="Summary">
        <div className={styles.sentenceFrame}>
          <TriggerSentence record={record} />
        </div>
      </FormSection>

      <FormSection
        title="Fires on"
        hint="What makes this trigger run."
        actions={
          subscription !== null && (
            <FormLinkButton
              onClick={() => setSampleOpen(true)}
              data-cy="view-sample-event"
            >
              View sample payload
            </FormLinkButton>
          )
        }
      >
        {schedule !== null ? (
          <FormField label="On a schedule">
            <p className={styles.value} data-cy="trigger-schedule">
              {readableSchedule(
                scheduleFromUtcCron(schedule.cron, browserTimeZone()),
              )}
            </p>
            <p className={styles.help}>
              Stored as <code className={styles.mono}>{schedule.cron}</code>,{" "}
              {readableCron(schedule.cron)}. Minutes missed during an outage are
              skipped, not caught up. If a target action is deleted, the trigger
              is disabled after the failed run.
            </p>
          </FormField>
        ) : (
          subscription !== null && (
            <>
              <FormRow>
                <FormField
                  label="Entity"
                  help="The kind of thing this trigger watches."
                >
                  <p className={styles.value}>
                    {entityLabel(subscription.events)}
                  </p>
                </FormField>
                <FormField label="Change" help="Any of these fires it.">
                  <div className={styles.chips}>
                    {subscription.events.map((eventType) => (
                      <span
                        key={eventType}
                        className={styles.chip}
                        title={eventType}
                      >
                        {eventLabel(eventType)}
                      </span>
                    ))}
                  </div>
                </FormField>
              </FormRow>

              <FormDivider />

              <FormField
                label="Fire once per"
                help={grainMeaning(subscription.events, subscription.once_per)}
              >
                <p className={styles.value}>
                  {grainLabel(subscription.events, subscription.once_per)}
                </p>
              </FormField>
            </>
          )
        )}
      </FormSection>

      <FormSection
        title="Conditions"
        hint={
          schedule !== null
            ? "Not available for schedules."
            : "Only fires when the subject matches all of these."
        }
        actions={
          clauseCount > 0 ? (
            <FormCount>
              {clauseCount === 1 ? "1 condition" : `${clauseCount} conditions`}
            </FormCount>
          ) : undefined
        }
        className={schedule !== null ? styles.inert : undefined}
        dataCy="conditions"
      >
        <ConditionsBody record={record} />
      </FormSection>

      <FormSection
        title="Targets"
        hint="What runs when the trigger fires, in order."
      >
        <div className={styles.targets}>
          {record.targets.map((target, index) => (
            <TargetCard
              key={target.target_id}
              target={target}
              index={index}
              firesOn={record.fires_on}
            />
          ))}
          {record.targets.length === 0 && (
            <p className={styles.muted}>This trigger has no targets.</p>
          )}
        </div>
      </FormSection>

      <FormSection title="Details">
        <TriggerFacts record={record} />
      </FormSection>

      {/* Mounted only while open: the dialog fetches the samples on mount,
          and most visits never open it. */}
      {sampleOpen && (
        <SampleEventDialog
          open
          onClose={() => setSampleOpen(false)}
          events={sourceEventTypes(record.fires_on)}
        />
      )}

      <AlertDialog
        dialogOpen={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        dialogTitle={"Delete Trigger"}
        dialogText={`Are you sure you want to delete the trigger '${record.name}'? This cannot be undone.`}
        dialogAction={() =>
          new Promise<void>((resolve) => {
            deleteTrigger.mutate(
              { name: record.name },
              {
                onError: (error) => {
                  setErrorText(error.message);
                  resolve();
                },
                onSuccess: () => {
                  resolve();
                  goto.triggers();
                },
              },
            );
          })
        }
      />
    </div>
  );
}

/** The condition read as prose, or why there is none. */
function ConditionsBody({ record }: { record: TriggerV2Record }) {
  if (isSchedule(record.fires_on)) {
    return <p className={styles.muted}>Schedules have nothing to filter on.</p>;
  }
  if (record.condition === null || record.condition === undefined) {
    return (
      <p className={styles.muted}>
        None &mdash; every matching change fires this trigger.
      </p>
    );
  }
  return (
    <>
      <p className={styles.prose}>{readableCondition(record.condition)}</p>
      {/* The prose is the readable form; the stored shape stays one click
          away for anyone reconciling it against the SDK or an API response. */}
      <details className={styles.raw}>
        <summary>Stored form</summary>
        <pre>{JSON.stringify(record.condition, null, 2)}</pre>
      </details>
    </>
  );
}

/**
 * One target, in the same card the editor uses: a mono "Target N" pill, its
 * type, the thing it acts on, then the configuration that decides what it
 * actually does.
 */
function TargetCard({
  target,
  index,
  firesOn,
}: {
  target: TriggerTargetSpec;
  index: number;
  firesOn: TriggerSource;
}) {
  return (
    <div className={styles.targetCard} data-cy="target-card">
      <div className={styles.targetCardHeader}>
        <span className={styles.targetPill}>Target {index + 1}</span>
        <span className={styles.targetType}>
          {TARGET_TYPE_LABELS[target.type]}
        </span>
        <TargetName target={target} />
      </div>
      <TargetDetail target={target} firesOn={firesOn} />
    </div>
  );
}

/** "File", or "Several" for an SDK-authored subscription spanning entities. */
function entityLabel(events: readonly PlatformEventType[]): string {
  const entity = entityOfSubscription(events);
  return entity === null ? "Several" : labelOfEntity(entity);
}

/** The chosen grain's label, as the editor's radios show it. */
function grainLabel(
  events: readonly PlatformEventType[],
  oncePer: OncePer,
): string {
  return (
    oncePerChoices(events).find((choice) => choice.value === oncePer)?.label ??
    oncePer
  );
}

function grainMeaning(
  events: readonly PlatformEventType[],
  oncePer: OncePer,
): string {
  return (
    oncePerChoices(events).find((choice) => choice.value === oncePer)
      ?.description ?? ""
  );
}

function TargetName({ target }: { target: TriggerTargetSpec }) {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return <span className={styles.targetName}>{target.action.name}</span>;
    case TriggerTargetType.StartAgent:
      return (
        <span className={styles.targetName}>
          <AgentLink agentId={target.agent_id} />
        </span>
      );
    case TriggerTargetType.SendSlackMessage:
      return <SlackChannelLink channelId={target.channel_id} />;
  }
}

function TargetDetail({
  target,
  firesOn,
}: {
  target: TriggerTargetSpec;
  firesOn: TriggerSource;
}) {
  switch (target.type) {
    case TriggerTargetType.InvokeAction: {
      return (
        <dl className={styles.facts}>
          <dt>Runs on</dt>
          <dd>{runsOnPhrase(target, firesOn)}</dd>
          {target.action.digest !== null &&
            target.action.digest !== undefined && (
              <>
                <dt>Pinned digest</dt>
                <dd className={styles.mono}>{target.action.digest}</dd>
              </>
            )}
        </dl>
      );
    }
    case TriggerTargetType.StartAgent:
      return (
        <dl className={styles.facts}>
          <dt>Thread visibility</dt>
          <dd>{target.visibility}</dd>
          {Object.keys(target.values).length > 0 && (
            <>
              <dt>Values</dt>
              <dd className={styles.mono}>
                {Object.entries(target.values)
                  .map(([name, value]) => `${name} = ${value}`)
                  .join(", ")}
              </dd>
            </>
          )}
        </dl>
      );
    case TriggerTargetType.SendSlackMessage:
      return (
        <dl className={styles.facts}>
          <dt>Message</dt>
          <dd className={styles.message}>{target.text}</dd>
        </dl>
      );
  }
}
